import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Tooltip, Typography as T } from "@mui/material";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { IconButton, InputAdornment } from "@mui/material";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Visibility from "@mui/icons-material/Visibility";
import { colors } from "../../../../styles";
import { adminLoginFullPath } from "../../../../routes/paths";
import AuthService from "../../../../api/services/AuthService";
import {
  QueryAdminCompanySettingsResponse,
  TeamMemberSetting,
} from "../../../../types";
import useGetCompanySettings from "../../../../hooks/queries/admin/useGetCompanySettings";
import useSaveCompanySettings from "../../../../hooks/mutations/admin/useSaveCompanySettings";
import useGetTeamSettings from "../../../../hooks/queries/admin/useGetTeamSettings";
import useChangePassword from "../../../../hooks/mutations/admin/useChangePassword";
import { useStoreActions } from "../../../../store";
import {
  PageContainer,
  ContentContainer,
  HeaderGradient,
  InviteProgramAdminsBox,
  SettingsTableActionsMenu,
} from "../components";
import logoutIcon from "../../../../assets/icons/logoutIcon.svg";
import { TextInput, FieldHeader } from "../../../../components/General";
import Button from "../../../../components/Button";
import Loader from "../../../../components/Loader";
import ErrorPage from "../../../../components/ErrorPage";
import { ReactComponent as FileExchangeIcon } from "../../../../assets/icons/fileExchangeArrows.svg";
import TableContainer from "../../../../components/TableContainer";
import {
  TableCellHeader,
  TableCellRow,
} from "../../../../components/TableCell";
import { getDataByPage } from "../../../../helpers/data";
import useGetMe from "../../../../hooks/queries/useGetMe";
import { CONTACT_SUPPORT_MSG } from "../../../../helpers/content";

// types
type Props = {};
type Page = "Company" | "Payroll" | "Billing" | "Team" | "Account";

// components
const LogoutButton = styled("button")(`
  position: absolute; 
  cursor: pointer;
  top: 30px;
  right: 30px;
  display: flex;
  justify-content: center;
  border: 1px solid #ececec;
  border-radius: 5px;
  padding: 6px 20px;
  width: 130px;
  background: white;
  cursor: pointer;
  transition: 1s;
  :hover {
    background: #ececec;
  }
`);

const ItemNav = ({
  title,
  handleClick,
  activePage,
}: {
  title: Page;
  handleClick: () => void;
  activePage: Page;
}) => {
  const isActive = activePage === title;
  return (
    <div
      className="flex items-center"
      style={{
        height: 36,
        borderBottom: isActive
          ? `2px solid ${colors.black}`
          : "2px solid white",
        cursor: "pointer",
      }}
      onClick={handleClick}
    >
      <T
        variant="mdMed"
        color={isActive ? `${colors.black}` : `${colors.gray_600}`}
      >
        {title}
      </T>
    </div>
  );
};

const defaultCompanyValues: {
  name: QueryAdminCompanySettingsResponse["name"];
  phone_number: QueryAdminCompanySettingsResponse["phone_number"];
} = {
  name: "",
  phone_number: "",
};

const Company = () => {
  const [values, setValues] = useState(defaultCompanyValues);
  const { data, status } = useGetCompanySettings();
  const { mutate: saveCompanySettings } = useSaveCompanySettings();

  useEffect(() => {
    if (data) {
      setValues({
        name: data.name,
        phone_number: data.phone_number,
      });
    }
  }, [data]);

  const setValue = (field: string, value: string) =>
    setValues((old) => ({ ...old, [field]: value }));

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    saveCompanySettings(values);
  };

  if (status === "loading") return <Loader />;
  if (status === "error") return <ErrorPage />;

  return (
    <form onSubmit={handleSubmit} className="w-50">
      <FieldHeader>Company Legal Name</FieldHeader>
      <TextInput
        size="small"
        value={values.name}
        onChange={(e) => setValue("name", e.target.value)}
        required
      />
      <FieldHeader>Company Phone Number</FieldHeader>
      <TextInput
        size="small"
        value={values.phone_number}
        onChange={(e) => setValue("phone_number", e.target.value)}
        required
      />
      <Button type="submit">Save</Button>
    </form>
  );
};

const Billing = () => {
  return (
    <T variant="mdReg">
      We're happy to help with billing information. Please{" "}
      <Tooltip title={CONTACT_SUPPORT_MSG}>
        <span className="link">contact support </span>
      </Tooltip>
      with any billing questions.
    </T>
  );
};

const Payroll = () => {
  const modalActions = useStoreActions((actions) => actions.modals);
  const { data, status } = useGetCompanySettings();

  if (status === "loading") return <Loader />;
  if (status === "error") return <ErrorPage />;

  return (
    <div className="w-100">
      <div
        className="flex flex-row items-center mb-16"
        style={{
          width: 560,
          height: 213,
          border: `1px solid ${colors.gray_01}`,
        }}
      >
        <div
          className="flex flex-col items-center justify-center"
          style={{
            height: "100%",
            width: "30%",
            background: `${colors.gradient_01}`,
          }}
        >
          <FileExchangeIcon />
          <T variant="lgMed">File Exchange</T>
        </div>
        <div style={{ width: "70%", padding: 24 }}>
          <T variant="lgMed">
            For {data.payroll_system} customers, we support a manual file
            exchange process.
          </T>
          <T variant="mdReg" sx={{ marginTop: "24px" }}>
            We will send a file every pay period with new employee enrollments
            and updated contributions.
          </T>
        </div>
      </div>
      <T variant="mdReg">
        To request changes to your file exchange process, please{" "}
        <span
          className="link"
          onClick={() => modalActions.setShowSupportModal(true)}
        >
          {" "}
          contact our team.
        </span>
      </T>
    </div>
  );
};

const Team = () => {
  const { data, status } = useGetTeamSettings();
  const [page, setPage] = useState(0);

  const rowsPerPage = 5;

  if (status === "loading") return <Loader />;
  if (status === "error") return <ErrorPage />;

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  return (
    <div className="flex flex-col mb-32">
      <InviteProgramAdminsBox />
      <div className="mt-16">
        <TableContainer title="Manage Administrators">
          <Table sx={{ minWidth: 650 }} aria-label="table header">
            <TableHead sx={{ background: "#f9fafb", height: 47 }}>
              <TableRow>
                <TableCellHeader>First</TableCellHeader>
                <TableCellHeader>Last</TableCellHeader>
                <TableCellHeader>Email</TableCellHeader>
                <TableCellHeader>Access</TableCellHeader>
                <TableCellHeader>Invite Status</TableCellHeader>
                <TableCellHeader size="small">Actions</TableCellHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {(data.count > 0
                ? getDataByPage(data.results, page, rowsPerPage)
                : []
              ).map((row: TeamMemberSetting) => {
                return (
                  <TableRow
                    key={row.id}
                    sx={{
                      "&:last-child td, &:last-child th": { border: 0 },
                    }}
                  >
                    <TableCellRow component="th" scope="row">
                      {row.user.first_name}
                    </TableCellRow>
                    <TableCellRow component="th" scope="row">
                      {row.user.last_name}
                    </TableCellRow>
                    <TableCellRow component="th" scope="row">
                      {row.user.email}
                    </TableCellRow>
                    <TableCellRow component="th" scope="row">
                      {row.access}
                    </TableCellRow>
                    <TableCellRow component="th" scope="row">
                      {row.invite_status}
                    </TableCellRow>
                    <TableCellRow component="th" scope="row" size="small">
                      <SettingsTableActionsMenu data={row} />
                    </TableCellRow>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[rowsPerPage]}
            rowsPerPage={rowsPerPage}
            component="div"
            count={data.count}
            page={page}
            onPageChange={handleChangePage}
            sx={{ background: "#f9fafb" }}
          />
        </TableContainer>
      </div>
    </div>
  );
};

const Account = () => {
  const { data, status } = useGetMe();
  const { mutate: changePassword } = useChangePassword({
    onSuccessHandler: () => {
      setOldPassword("");
      setNewPassword("");
      setConfirmNewPassword("");
    },
  });
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  if (status === "loading") return <Loader />;
  if (status === "error") return <ErrorPage />;

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (newPassword !== confirmNewPassword) {
      toast.error("New passwords do not match");
      return;
    }
    changePassword({ old_password: oldPassword, new_password: newPassword });
  };

  return (
    <div className="flex flex-col w-50">
      <div className="mb-16">
        <T variant="lgMed" sx={{ marginBottom: "8px" }}>
          Name
        </T>
        <T variant="mdReg">
          {data.user.first_name} {data.user.last_name}
        </T>
      </div>
      <div className="mb-16">
        <T variant="lgMed" sx={{ marginBottom: "8px" }}>
          Email
        </T>
        <T variant="mdReg">{data.user.email}</T>
      </div>
      <T variant="lgMed" sx={{ marginBottom: "8px" }}>
        Change Password
      </T>
      <form onSubmit={handleSubmit}>
        <FieldHeader>Old password</FieldHeader>
        <TextInput
          type={showOldPassword ? "text" : "password"}
          size="small"
          required
          value={oldPassword}
          onChange={(e) => setOldPassword(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowOldPassword(!showOldPassword)}
                  size={"small"}
                  edge="end"
                >
                  {showOldPassword ? (
                    <VisibilityOff fontSize="small" />
                  ) : (
                    <Visibility fontSize="small" />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <FieldHeader>New password</FieldHeader>
        <TextInput
          type={showNewPassword ? "text" : "password"}
          size="small"
          required
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  size={"small"}
                  edge="end"
                >
                  {showNewPassword ? (
                    <VisibilityOff fontSize="small" />
                  ) : (
                    <Visibility fontSize="small" />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <FieldHeader>Confirm new password</FieldHeader>
        <TextInput
          type={showConfirmPassword ? "text" : "password"}
          size="small"
          required
          value={confirmNewPassword}
          onChange={(e) => setConfirmNewPassword(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  size={"small"}
                  edge="end"
                >
                  {showConfirmPassword ? (
                    <VisibilityOff fontSize="small" />
                  ) : (
                    <Visibility fontSize="small" />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Button sx={{ marginTop: "16px" }} type="submit">
          Update password
        </Button>
      </form>
    </div>
  );
};

const Settings = (props: Props) => {
  const queryClient = useQueryClient();

  const [page, setPage] = useState<Page>("Company");
  const navigate = useNavigate();

  const handleLogout = () => {
    queryClient.invalidateQueries();
    AuthService.logout();
    navigate(adminLoginFullPath);
  };
  return (
    <PageContainer>
      <HeaderGradient
        title="Settings"
        subtitle="Review and update ESA settings"
      />
      <LogoutButton onClick={handleLogout}>
        <img src={logoutIcon} alt="logout" />
        &nbsp; Logout
      </LogoutButton>
      <ContentContainer>
        <div className="flex flex-row gap-16">
          <ItemNav
            title="Company"
            handleClick={() => setPage("Company")}
            activePage={page}
          />
          <ItemNav
            title="Payroll"
            handleClick={() => setPage("Payroll")}
            activePage={page}
          />
          <ItemNav
            title="Billing"
            handleClick={() => setPage("Billing")}
            activePage={page}
          />
          <ItemNav
            title="Team"
            handleClick={() => setPage("Team")}
            activePage={page}
          />
          <ItemNav
            title="Account"
            handleClick={() => setPage("Account")}
            activePage={page}
          />
        </div>
        <div className="flex flex-col mt-32">
          {page === "Company" && <Company />}
          {page === "Billing" && <Billing />}
          {page === "Payroll" && <Payroll />}
          {page === "Team" && <Team />}
          {page === "Account" && <Account />}
        </div>
      </ContentContainer>
    </PageContainer>
  );
};

export default Settings;
